/* BI-01 · PLANS IN-APP — real tier.
   L5: plans are described by Locations and capability; the participation
   allocation is an abuse ceiling, never a value lever. L4: Free includes the
   Google review invitation. FQ-I4: no Growth (« NO Growth », 08/10) — the ladder is what V1 sells, from plans.js. */
import { useNavigate } from 'react-router-dom'
import { Button, Card, Note, Pill } from '@/ui'
import { getPlan, planAllows, type Plan } from '@/lib/campaign'
import { PLAN_LABELS, SOLD_PLANS, discount, planCampaigns, planOffer, planPrice } from '@/lib/plans'
import { Head, useBill } from './ParamShell'

/* v3.3 · WF-041 · what each plan does, one card per sold plan. Every figure is plans.js's through lib/plans
   (campaigns online at once: Free 1 · Starter 3) or lib/campaign's planAllows; nothing restated. The reference's visit
   counts (« 1 visite », « jusqu’à 6 visites ») have no source in the app's plan data: not shown (⟦confirmable⟧).
   Ruled: en ligne, programmées and en pause occupy a place, « Terminer » frees it, « Reprendre » is never refused. */
const PLAN_TAGLINE: Record<Plan, string> = { free: 'Votre premier parcours', starter: 'Plus de parcours, plus de choix', multi: 'Plusieurs établissements' }
const campaignsLine = (p: Plan) => { const n = planCampaigns(p); return n === null ? 'Campagnes à la fois : sans plafond' : `${n} campagne${n > 1 ? 's' : ''} à la fois` }
const PLACE_RULE = 'En ligne, programmées et en pause occupent une place. Terminer la libère ; « Reprendre » n’est jamais refusé.'
function bulletsOf(p: Plan): string[] {
  const b: string[] = []
  b.push(planAllows(p, 'pendant') ? 'Demande pendant la roue, ou après le cadeau' : 'Demande Google après le cadeau')
  b.push(planAllows(p, 'platforms') ? 'Toutes les demandes et plateformes · répartition entre plateformes au sein d’une seule demande' : 'La boucle cadeau et validation en caisse')
  if (planAllows(p, 'roue-reseau')) b.push('Une roue pour tout le réseau')
  b.push('Brouillons conservés lorsque l’offre est pleine')
  b.push(PLACE_RULE)
  return b
}

export default function BI01Offre() {
  const b = useBill()
  const nav = useNavigate()
  const plan = getPlan()
  return (
    <section data-frame="BI-01" style={{ display: 'grid', gap: 14 }}>
      <Head title="Votre offre"
        end={<Pill tone={b.planBadge.tone} mark={b.planBadge.mark}>{b.planBadge.label}</Pill>} />
      {/* WF-041 · one card per plan sold (plus the world's own plan when it is not sold): what it allows, and the place rule */}
      <div data-testid="offre-plans" style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', alignItems: 'start' }}>
        {(SOLD_PLANS.includes(plan) ? SOLD_PLANS : [...SOLD_PLANS, plan]).map(p => (
          <Card key={p} data-testid={`offre-plan-${p}`}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Pill tone={p === plan ? 'ok' : 'done'}>{planOffer(p)}</Pill>
              {p === plan && <span className="f-help">votre offre</span>}
            </div>
            <h3 className="t-section" style={{ margin: '10px 0 2px' }}>{PLAN_TAGLINE[p]}</h3>
            <p className="t-body" style={{ margin: '0 0 8px' }}><b>{campaignsLine(p)}</b> · {planPrice(p)}</p>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4, fontSize: 13 }}>
              {bulletsOf(p).map(x => <li key={x}>{x}</li>)}
            </ul>
          </Card>
        ))}
      </div>
      <Note tone="ok" data-testid="offre-regle">
        <strong>Offre actuelle&nbsp;: {PLAN_LABELS[plan]}.</strong> {PLACE_RULE}
      </Note>
      <div className="tablewrap">
        <table className="tbl">
          <thead><tr><th>Offre</th><th>Prix</th><th>Établ.</th><th>Participations</th></tr></thead>
          <tbody>
            {b.plans.map(p => (
              <tr key={p.name}>
                <td>{p.strong ? <strong>{p.name}</strong> : p.name}</td>
                <td>{p.price}</td>
                <td>{p.locationsStrong ? <strong>{p.locations}</strong> : p.locations}</td>
                <td>{p.includesLead ? <><strong>{p.includesLead}</strong>{p.includes}</> : p.includes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="t-body">
        {discount('starter', 'yearly')} en annuel · {discount('starter', '3y')} sur 3 ans. Les offres se choisissent sur les établissements et les
        fonctionnalités ; l’allocation de participations est un plafond anti-abus, pas un compteur
        de valeur.
      </p>
      <Note tone="candour">
        <strong>Offre gratuite :</strong> l’invitation à laisser un avis Google est incluse (cadeau
        immédiat). « Avis pendant que la roue tourne » et les autres plateformes
        arrivent avec Starter.
      </Note>
      {/* WF-041 · « Des coûts distincts »: what BMR bills, what it never bills. No price is invented: the subscription's is
          plans.js's; the gifts' cost is the merchant's own; orders of material are DEFERRED, a download orders nothing. */}
      <Card data-testid="couts-distincts">
        <p className="t-eyebrow" style={{ marginTop: 0 }}>Des coûts distincts</p>
        {/* v2 · 390 px: see EffetTable — the table's 480 px min-width must not size the page's grid track */}
        <div className="tablewrap" style={{ contain: 'inline-size' }}>
          <table className="tbl">
            <thead><tr><th>Coût</th><th>Ce qui est connu</th></tr></thead>
            <tbody>
              <tr><td>Abonnement BMR</td><td>{planPrice(plan)} — le seul montant facturé par BMR. Le nombre de personnes dans l’équipe n’est pas facturé.</td></tr>
              <tr><td>Cadeaux offerts</td><td>Vos coûts de réalisation estimés, privés. Ce ne sont pas des frais BMR.</td></tr>
              <tr><td>Impression / NFC / écran</td><td>Le téléchargement ne commande aucun matériel.</td></tr>
            </tbody>
          </table>
        </div>
      </Card>
      {/* FQ-I4 · the Offre page's actions, as buttons (no longer tabs): the free plan passes to Starter through its trial */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} data-testid="offre-actions">
        {plan === 'free'
          ? <Button variant="primary" size="sm" onClick={() => nav('/parametres/essai')}>Passer à Starter</Button>
          : <>
              <Button variant="primary" size="sm" onClick={() => nav('/parametres/offre/annuel')}>Passer à l’annuel</Button>
              {plan === 'multi' && <>
                <Button size="sm" onClick={() => nav('/parametres/etablissement')}>Ajouter un établissement</Button>
                <Button size="sm" onClick={() => nav('/parametres/offre/starter')}>Repasser en Starter</Button>
              </>}
              <Button variant="quiet" size="sm" onClick={() => nav('/parametres/annuler')}>Annuler l’abonnement</Button>
            </>}
      </div>
    </section>
  )
}
